import { useCallback, useContext, useEffect, useState } from "react";
import { useSound } from "../hooks";
import { setDilemaCard } from "../components/molecules/Board/helpers";
import { BoardContext, PhaseContext, SocketContext, TableContext, TeamContext, UserContext, FabricContext } from "../context";
import { ICard, IChallengeCardsBoard } from "../interfaces";
import { CardType, Phase } from "../util/enums";
import { useTranslation } from "react-i18next";

type displayViewType = "initial" | "chooseChallengeCard" | "action" | "question" | "solutionView" | "fortuneWheel" | "teamCelebration" | "giverCelebration";
type challengeCardType = "questionCard" | "actionCard";

const useBoardDisplayView = (): [IChallengeCardsBoard, displayViewType, (fieldName: challengeCardType, card: ICard) => void] => {
    const { t } = useTranslation();
    const { 
        socket, 
        drawDilemmaCardSocketEvent, 
        setIsSpinClicked, 
        setSpinNumberData, 
        nextPhaseConffeti, 
        revealDoneButtonSocketOn,
        refreshTimerSocket,
        setIsActionDoneButtonRevealed
    } = useContext(SocketContext);
    const { setCanvasToUndefined } = useContext(FabricContext);
    const { game, activateAction, setActivateAction } = useContext(TeamContext);
    const { userData, setNotificationAlert, setActivateAlert } = useContext(UserContext);
    const {
        areSolutionCardsDrawnContext,
        setIsInitialSolutionDecksModalOpen,
        setRenderDilemmaCardsInThisPhase,
        setRenderInitialCoins,
        setIsAvatarCheering,
        setIsCurrentlyPhase16,
        setIsDilemmaOwnerAnouncementDone,
        setAreStrengthsShownFullScreen
    } = useContext(BoardContext)
    const { setRenderChosenSolutionToQuestion,
        setRenderDilemmaOwnerCoinCelebration,
        setRenderWheelOfFortune
    } = useContext(PhaseContext);
    const { setAreAnimatedHandsShown, 
        setIsLogoOnTheTableBlured, 
        setDidTheCurrentUserProposedSolution, 
        setIsDilemmaChosen,
        setToggleMySolutionFullScreen
    } = useContext(TableContext);
    const [displayView, setDisplayView] = useState<displayViewType>("initial");
    const [challengeCard, setChallengeCard] = useState<IChallengeCardsBoard>({
        questionCard: null,
        actionCard: null
    });
    const isTheUserDilemaOwner = userData?.email === game?.gameInfo.currentPlayerEmail;
    const {playSound} = useSound();

    useEffect(() => {
        
        if (game?.phase === Phase.CHOOSING_SOLUTION_CARDS) {
            setActivateAction(false);
            setRenderDilemmaCardsInThisPhase(false);
            setIsInitialSolutionDecksModalOpen(true);
        }
    }, [game?.phase, setActivateAction, setIsInitialSolutionDecksModalOpen, setRenderDilemmaCardsInThisPhase]);

    useEffect(() => {
        if (game?.phase === Phase.CONFETTI_CELEBRATION)
        setAreStrengthsShownFullScreen(false);
    }, [game?.phase, setAreStrengthsShownFullScreen])

    useEffect(() => {
        const grandCoinCelebrationTime = 5000;

        if (game?.phase === Phase.CHOOSING_SOLUTION_CARDS && areSolutionCardsDrawnContext) {
            setRenderInitialCoins(true);

            const timeout = setTimeout(() => {
                setRenderInitialCoins(false);
            }, grandCoinCelebrationTime);
            
            return () => clearTimeout(timeout);
        }
    }, [areSolutionCardsDrawnContext, game?.phase, setRenderInitialCoins]);

    useEffect(() => {
        if (game?.phase === Phase.QUESTION_CARD && game?.gameInfo.players[game?.gameInfo.currentPlayerEmail].finishedAction )
            setIsDilemmaChosen(true);
    }, [game?.gameInfo.currentPlayerEmail, game?.gameInfo.players, game?.phase, setIsDilemmaChosen]);

    useEffect(() => {
        if (game?.phase === Phase.INTRODUCTION) { 
            setIsCurrentlyPhase16(true);

        }
    }, [game?.phase, setAreAnimatedHandsShown, setIsCurrentlyPhase16]);

    useEffect(() => {
        const timeBetweenInitialTableAndRenderingDilemmaCards = 3000;

        if (game?.phase !== Phase.CHOOSING_SOLUTION_CARDS) 
            setIsInitialSolutionDecksModalOpen(false);

        if (game?.phase === Phase.PICK_DILEMMA) {
            setDisplayView("chooseChallengeCard");
            setCanvasToUndefined();

            const renderDilemaCardsTimeout =
                setTimeout(() => {
                    playSound("/sounds/PositiveAlert3.mp3")
                    setRenderDilemmaCardsInThisPhase(true);

                }, timeBetweenInitialTableAndRenderingDilemmaCards);

            return () => clearTimeout(renderDilemaCardsTimeout);
        }

        
    }, [game?.phase, playSound, setAreAnimatedHandsShown, setCanvasToUndefined, setIsInitialSolutionDecksModalOpen, setRenderDilemmaCardsInThisPhase]);

    useEffect(() => {
        // if (game?.phase === Phase.QUESTION_CARD && !isTheUserDilemaOwner)
        //     setAreAnimatedHandsShown(true);

        if (game?.phase === Phase.QUESTION_CARD)
            setSpinNumberData(undefined);
    }, [game?.phase, isTheUserDilemaOwner, setAreAnimatedHandsShown, setSpinNumberData]);

    useEffect(() => {
        if (game?.phase === Phase.QUESTION_CARD )
            setToggleMySolutionFullScreen(true);
            setDidTheCurrentUserProposedSolution(false);
    }, [game?.phase, setDidTheCurrentUserProposedSolution, setToggleMySolutionFullScreen]);

    useEffect(() => {
        if (game?.phase === Phase.CONFETTI_CELEBRATION) {
            setDisplayView("teamCelebration");
            setIsAvatarCheering(true);
        }

        if (game?.phase !== Phase.CONFETTI_CELEBRATION)
            setIsAvatarCheering(false);
    }, [game?.phase, setIsAvatarCheering]);

    useEffect(() => {
        if(game?.phase === Phase.PICK_DILEMMA){
            setIsDilemmaOwnerAnouncementDone("");
            setIsDilemmaChosen(false);
        }
    }, [game?.phase, setIsDilemmaChosen, setIsDilemmaOwnerAnouncementDone] );

    useEffect(() => {
        if (game?.phase === Phase.CHOOSE_SOLUTION &&  game?.gameInfo.challengeScore > 0) 
            setIsDilemmaOwnerAnouncementDone("renderTippingHeart");
    }, [game?.gameInfo.challengeScore, game?.phase, setIsDilemmaOwnerAnouncementDone]);

    useEffect(() => {
        if (game?.phase === Phase.SOLUTION_CHOSEN)
            setDisplayView("initial");
    }, [game]);

    useEffect(() => {

        if (game?.phase === Phase.ACTION_CARD || game?.phase === Phase.QUESTION_CARD) {
            setIsLogoOnTheTableBlured(true);
        } else {
            setIsLogoOnTheTableBlured(false);
        }

    }, [game?.phase, setIsLogoOnTheTableBlured]);

    
    useEffect(() => {
        if (game?.phase === Phase.CHOOSING_SOLUTION_CARDS && displayView !== "initial") {
            setIsSpinClicked(false);
            
            if (activateAction) {
                setDisplayView("teamCelebration");
                setTimeout(() => {
                    return setDisplayView("initial");
                }, 7000);
            }
        }
    }, [game, setIsSpinClicked, activateAction, displayView]);

    useEffect(() => { 
        if (game?.phase === Phase.CHOOSING_SOLUTION_CARDS && displayView !== "initial") {
            setIsSpinClicked(false);
            
            if (displayView !== "fortuneWheel")
                setDisplayView("initial");
            }
        }, [game, setIsSpinClicked, displayView]);
        
        useEffect(() => {
        if (game?.phase === Phase.ACTION_CARD && displayView !== "action") {
            setIsActionDoneButtonRevealed(false);
            refreshTimerSocket();
            revealDoneButtonSocketOn();
            setDisplayView("action");
            setChallengeCard({
                ...challengeCard,
                actionCard: setDilemaCard(game?.gameInfo.currentDilemmaCard, CardType.ACTION)
            });
        }
        // if I add "challengeCard" as a dependancy in the same useEffect where I have setChallengeCard, it will reRender forever
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game, displayView]);
    
    useEffect(() => {
        if (game?.phase === Phase.QUESTION_CARD && displayView !== "question") {
            setDisplayView("question");
            setChallengeCard({
                ...challengeCard,
                questionCard: setDilemaCard(game?.gameInfo.currentDilemmaCard, CardType.QUESTION)
            });
        }
        // if I add "challengeCard" as a dependancy in the same useEffect where I have setChallengeCard, it will reRender forever
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game, displayView]);
    
    useEffect(() => {
        if (game?.phase === Phase.CHOOSE_SOLUTION)
        setDisplayView("question");
    }, [game]);
    
    useEffect(() => {
        if (game?.phase === Phase.SOLUTION_CHOSEN)
            setRenderChosenSolutionToQuestion(true);

        if (game?.phase !== Phase.SOLUTION_CHOSEN)
            setRenderChosenSolutionToQuestion(false);
    }, [game, setRenderChosenSolutionToQuestion])

    useEffect(() => {
        if (game?.phase === Phase.SOLUTION_CHOSEN_REWARD) {
            setRenderChosenSolutionToQuestion(false);
            setRenderDilemmaOwnerCoinCelebration(true);
            nextPhaseConffeti(6000);
        }
        // if I add "nextPhaseConffeti" cheerButton will not work (conffetti phase will not work)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game?.phase, setRenderChosenSolutionToQuestion, setRenderDilemmaOwnerCoinCelebration])
    
    useEffect(() => {
        if (game?.phase !== Phase.SOLUTION_CHOSEN_REWARD)
        setRenderDilemmaOwnerCoinCelebration(false);
    }, [game?.phase, setRenderDilemmaOwnerCoinCelebration]);
    
    useEffect(() => {
        if (game?.phase === Phase.RENDER_SPIN_WHEEL) {
            setRenderDilemmaOwnerCoinCelebration(false);
            setRenderWheelOfFortune(true);
            socket?.emit("finishActionCard");
            nextPhaseConffeti(6000);
        }
        // if I add socket as a dependancy, it will reRenderendlessly
        // if I add "nextPhaseConffeti" cheerButton will not work (conffetti phase will not work)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [game?.phase, setRenderDilemmaOwnerCoinCelebration, setRenderWheelOfFortune]);
    
    useEffect(() => {
        if (game?.phase !== Phase.RENDER_SPIN_WHEEL)
        setRenderWheelOfFortune(false);
    }, [game?.phase, setRenderWheelOfFortune]);

    
    useEffect(() => {
        const proposedCards = game?.gameInfo?.proposedSolutionCards;

        for (const key in proposedCards) {  
            if(key === userData?.email) 
            setDidTheCurrentUserProposedSolution(true);
          }
    }, [game?.gameInfo?.proposedSolutionCards, setDidTheCurrentUserProposedSolution, userData?.email]);
  
    useEffect(() => {
        setChallengeCard({
            questionCard: setDilemaCard(game?.gameInfo.currentDilemmaCard, CardType.QUESTION),
            actionCard: setDilemaCard(game?.gameInfo.currentDilemmaCard, CardType.ACTION)
        })
    }, [game]);
    
    const drawChallengeCard = useCallback((fieldName: challengeCardType, card: ICard) => {
        setChallengeCard({
            ...challengeCard,
            [fieldName]: card
        });

        if (!isTheUserDilemaOwner) {
            setNotificationAlert({message: t("youAreNotADillemaOwnerText"), type:"regular"});
            setActivateAlert(true);
            return;
        }

        if (fieldName === "actionCard") {
            drawDilemmaCardSocketEvent("Action");
            setDisplayView("action");
        }

        if (fieldName === "questionCard") {
            drawDilemmaCardSocketEvent("Question");
            setDisplayView("question");
        }
    }, [challengeCard, drawDilemmaCardSocketEvent, setNotificationAlert, setActivateAlert]);

    return [challengeCard, displayView, drawChallengeCard];
}

export default useBoardDisplayView;